import React from 'react';
import { styled, keyframes, css } from '@compiled/react';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntersectionObserver } from '@atlassian/jira-intersection-observer/src/index.tsx';

type Props = {
	onVisible?: () => void;
};

export const DesignCardSkeleton = ({ onVisible }: Props) => {
	const onSetElementRef = useIntersectionObserver({
		margin: 0,
		onIntersection: () => onVisible?.(),
	});

	return (
		<Box xcss={cardContainerStyles} ref={onSetElementRef}>
			<SkeletonItem width="24px" height="24px" margin="0" />
			<Stack xcss={titleStackStyles}>
				<SkeletonItem width="220px" height="16px" margin="2px 0" />
				<SkeletonItem width="80px" height="12px" margin="2px 0" />
				<SkeletonItem width="120px" height="24px" margin="12px 0" />
				<SkeletonItem width="50px" height="12px" margin="2px 0" />
				<SkeletonItem width="240px" height="26px" margin="5px 0" />
			</Stack>
		</Box>
	);
};

interface SkeletonItemProps {
	height: string;
	width: string;
	margin: string;
}

const animationOptionStyles = css({
	animationDelay: "'0s'",
	animationDuration: '2s',
	animationFillMode: 'forwards',
	animationIterationCount: 'infinite',
	animationTimingFunction: 'linear',
	backgroundImage: `linear-gradient( to right, ${token('color.skeleton')} 10%, ${token('color.skeleton.subtle')} 30%, ${token('color.skeleton')} 50% )`,
	backgroundRepeat: 'no-repeat',
});

const shimmer = keyframes({
	'0%': {
		backgroundPosition: '-300px 0',
	},
	'100%': {
		backgroundPosition: '1700px 0',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const SkeletonItem = styled.div<SkeletonItemProps>(animationOptionStyles, {
	animationName: shimmer,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.skeleton'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${gridSize * 0.5}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props: SkeletonItemProps) => props.height,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: (props: SkeletonItemProps) => props.width,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	margin: (props: SkeletonItemProps) => `${props.margin}`,
});

const cardContainerStyles = xcss({
	display: 'flex',
	border: '3px',
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
	paddingLeft: 'space.150',
	paddingRight: 'space.150',
	boxShadow: 'elevation.shadow.raised',
});

const titleStackStyles = xcss({
	flex: 1,
	marginLeft: 'space.100',
	overflow: 'hidden',
});
